// Shared audio-processing helpers for the Adjust feature (live preview + export).
import lamejs from 'lamejs';

export const GAIN_MIN_DB = -12;
export const GAIN_MAX_DB = 36;
export const SPEED_PRESETS = [0.5, 0.75, 1, 1.25, 1.5, 2];
export const DEFAULT_ADJUST_SETTINGS = { noiseReduction: false, enhance: false, gainDb: 0, speed: 1 };

export const dbToGain = (db) => Math.pow(10, db / 20);

// How long a slice of a long loop may hog the main thread before it hands control back.
const SLICE_MS = 10;

// Hands the thread back so the browser can paint and handle clicks mid-render.
const yieldToUi = () => new Promise((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = () => {
        channel.port1.close();
        resolve();
    };
    channel.port2.postMessage(null);
});

// Builds the same node chain for a live AudioContext or an OfflineAudioContext.
export const buildEffectChain = (ctx, settings) => {
    const nodes = [];

    if (settings.noiseReduction) {
        // Band-limit to the voice range to drop rumble and hiss.
        const highpass = ctx.createBiquadFilter();
        highpass.type = 'highpass';
        highpass.frequency.value = 100;
        highpass.Q.value = 0.7;

        const lowpass = ctx.createBiquadFilter();
        lowpass.type = 'lowpass';
        lowpass.frequency.value = 8000;
        lowpass.Q.value = 0.7;

        nodes.push(highpass, lowpass);
    }

    if (settings.enhance) {
        // Presence EQ plus light compression for clearer speech.
        const lowShelf = ctx.createBiquadFilter();
        lowShelf.type = 'lowshelf';
        lowShelf.frequency.value = 150;
        lowShelf.gain.value = -3;

        const presence = ctx.createBiquadFilter();
        presence.type = 'peaking';
        presence.frequency.value = 3000;
        presence.Q.value = 1;
        presence.gain.value = 4;

        const air = ctx.createBiquadFilter();
        air.type = 'highshelf';
        air.frequency.value = 6000;
        air.gain.value = 2;

        const compressor = ctx.createDynamicsCompressor();
        compressor.threshold.value = -24;
        compressor.knee.value = 12;
        compressor.ratio.value = 3;
        compressor.attack.value = 0.005;
        compressor.release.value = 0.2;

        nodes.push(lowShelf, presence, air, compressor);
    }

    const gain = ctx.createGain();
    gain.gain.value = dbToGain(settings.gainDb || 0);
    nodes.push(gain);

    if ((settings.gainDb || 0) > 0) {
        // Soft limiter so boosted audio doesn't clip harshly.
        const limiter = ctx.createDynamicsCompressor();
        limiter.threshold.value = -1;
        limiter.knee.value = 0;
        limiter.ratio.value = 20;
        limiter.attack.value = 0.001;
        limiter.release.value = 0.1;
        nodes.push(limiter);
    }

    for (let i = 0; i < nodes.length - 1; i++) {
        nodes[i].connect(nodes[i + 1]);
    }

    return { input: nodes[0], output: nodes[nodes.length - 1] };
};

// WSOLA time-stretch: changes speed while preserving pitch.
const stretchChannel = async (input, sampleRate, speed) => {
    const frameSize = Math.round(sampleRate * 0.04);
    const synthesisHop = Math.floor(frameSize / 2);
    const analysisHop = synthesisHop * speed;
    const tolerance = Math.round(sampleRate * 0.008);
    const outputLength = Math.floor(input.length / speed);
    const output = new Float32Array(outputLength + frameSize);
    const window = new Float32Array(frameSize);

    for (let i = 0; i < frameSize; i++) {
        window[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (frameSize - 1));
    }

    const frameCount = Math.ceil(outputLength / synthesisHop);
    let previousStart = 0;
    let sliceStart = performance.now();

    for (let k = 0; k < frameCount; k++) {
        if (performance.now() - sliceStart > SLICE_MS) {
            await yieldToUi();
            sliceStart = performance.now();
        }

        const outPos = k * synthesisHop;
        const naturalPos = Math.round(k * analysisHop);
        let bestStart = naturalPos;

        if (k > 0) {
            // Where the previous frame would naturally continue in the input.
            const continuation = previousStart + synthesisHop;
            let bestScore = -Infinity;
            const minStart = Math.max(0, naturalPos - tolerance);
            const maxStart = Math.min(input.length - frameSize, naturalPos + tolerance);

            for (let candidate = minStart; candidate <= maxStart; candidate += 2) {
                let score = 0;
                for (let j = 0; j < synthesisHop; j += 4) {
                    score += input[continuation + j] * input[candidate + j];
                }
                if (score > bestScore) {
                    bestScore = score;
                    bestStart = candidate;
                }
            }
        }

        if (bestStart + frameSize > input.length) {
            bestStart = Math.max(0, input.length - frameSize);
        }

        for (let j = 0; j < frameSize; j++) {
            output[outPos + j] += input[bestStart + j] * window[j];
        }

        previousStart = bestStart;
    }

    return output.subarray(0, outputLength);
};

export const timeStretchBuffer = async (ctx, buffer, speed) => {
    if (!speed || speed === 1) return buffer;

    const outputLength = Math.floor(buffer.length / speed);
    const stretched = ctx.createBuffer(buffer.numberOfChannels, outputLength, buffer.sampleRate);

    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
        stretched.copyToChannel(await stretchChannel(buffer.getChannelData(channel), buffer.sampleRate, speed), channel);
    }

    return stretched;
};

// Renders the full adjusted audio offline and returns an AudioBuffer.
export const renderAdjustedBuffer = async (sourceBuffer, settings) => {
    const speed = settings.speed || 1;
    const outputLength = speed === 1 ? sourceBuffer.length : Math.floor(sourceBuffer.length / speed);

    const offlineCtx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(
        sourceBuffer.numberOfChannels,
        outputLength,
        sourceBuffer.sampleRate
    );

    const source = offlineCtx.createBufferSource();
    source.buffer = await timeStretchBuffer(offlineCtx, sourceBuffer, speed);

    const chain = buildEffectChain(offlineCtx, settings);
    source.connect(chain.input);
    chain.output.connect(offlineCtx.destination);
    source.start(0);

    return offlineCtx.startRendering();
};

export const audioBufferToWav = async (abuffer) => {
    const numOfChan = abuffer.numberOfChannels;
    const length = abuffer.length * numOfChan * 2 + 44;
    const buffer = new ArrayBuffer(length);
    const view = new DataView(buffer);
    const channels = [];
    let offset = 0;
    let pos = 0;

    const setUint16 = (data) => {
        view.setUint16(pos, data, true);
        pos += 2;
    };
    const setUint32 = (data) => {
        view.setUint32(pos, data, true);
        pos += 4;
    };

    setUint32(0x46464952);
    setUint32(length - 8);
    setUint32(0x45564157);
    setUint32(0x20746d66);
    setUint32(16);
    setUint16(1);
    setUint16(numOfChan);
    setUint32(abuffer.sampleRate);
    setUint32(abuffer.sampleRate * 2 * numOfChan);
    setUint16(numOfChan * 2);
    setUint16(16);
    setUint32(0x61746164);
    setUint32(length - pos - 4);

    for (let i = 0; i < numOfChan; i++) {
        channels.push(abuffer.getChannelData(i));
    }

    let sliceStart = performance.now();

    while (pos < length) {
        for (let i = 0; i < numOfChan; i++) {
            let sample = Math.max(-1, Math.min(1, channels[i][offset]));
            sample = (0.5 + sample < 0 ? sample * 32768 : sample * 32767) | 0;
            view.setInt16(pos, sample, true);
            pos += 2;
        }
        offset++;

        if ((offset & 0x3fff) === 0 && performance.now() - sliceStart > SLICE_MS) {
            await yieldToUi();
            sliceStart = performance.now();
        }
    }

    return new Blob([buffer], { type: 'audio/wav' });
};

const floatToInt16 = (data) => {
    const samples = new Int16Array(data.length);
    for (let i = 0; i < data.length; i++) {
        const sample = Math.max(-1, Math.min(1, data[i]));
        samples[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }
    return samples;
};

// Uploading raw wav blows past the size the storage provider accepts, so the export is encoded to mp3.
export const audioBufferToMp3 = async (abuffer, kbps = 128) => {
    if (typeof lamejs.MPEGMode === 'undefined') {
        lamejs.MPEGMode = { STEREO: 0, JOINT_STEREO: 1, DUAL_CHANNEL: 2, MONO: 3 };
    }

    // lamejs reads a right channel whenever it was built for two, so anything above stereo is dropped.
    const channels = abuffer.numberOfChannels >= 2 ? 2 : 1;
    const encoder = new lamejs.Mp3Encoder(channels, abuffer.sampleRate, kbps);
    const left = floatToInt16(abuffer.getChannelData(0));
    const right = channels === 2 ? floatToInt16(abuffer.getChannelData(1)) : null;
    const blockSize = 1152;
    const chunks = [];

    let sliceStart = performance.now();

    for (let i = 0; i < left.length; i += blockSize) {
        const encoded = channels === 2
            ? encoder.encodeBuffer(left.subarray(i, i + blockSize), right.subarray(i, i + blockSize))
            : encoder.encodeBuffer(left.subarray(i, i + blockSize));
        if (encoded.length > 0) chunks.push(encoded);

        if (performance.now() - sliceStart > SLICE_MS) {
            await yieldToUi();
            sliceStart = performance.now();
        }
    }

    const end = encoder.flush();
    if (end.length > 0) chunks.push(end);

    return new Blob(chunks, { type: 'audio/mpeg' });
};
